/**
 * ExponentialBackoff - A comprehensive exponential backoff utility class
 * Provides configurable retry logic with cancellation support and jitter options
 */
export class ExponentialBackoff {
  /**
   * Default configuration options
   */
  static DEFAULT_CONFIG = {
    initialInterval: 100,
    maxDelay: 30000,
    step: 2,
    maxRetries: 10,
    onRetry: null,
    onMaxRetriesReached: null,
    jitterPercent: 0
  };

  /**
   * Create an ExponentialBackoff instance
   * @param {Object} config - Configuration options
   * @param {number} config.initialInterval - Initial delay in milliseconds (default: 100)
   * @param {number} config.maxDelay - Maximum delay cap in milliseconds (default: 30000)
   * @param {number} config.step - Multiplication factor for each retry (default: 2)
   * @param {number|null} config.maxRetries - Maximum number of retry attempts (default: 10, null for infinite)
   * @param {Function} config.onRetry - Optional callback called on each retry attempt
   * @param {Function} config.onMaxRetriesReached - Optional callback when max retries exceeded
   * @param {number} config.jitterPercent - Percentage of jitter to add (0-100, default: 0)
   */
  constructor(config = {}) {
    this.config = { ...ExponentialBackoff.DEFAULT_CONFIG, ...config };
    this._validateConfig();
    
    // Instance state
    this.isCancelled = false;
    this.isRunning = false;
    this.currentTimeout = null;
    this.currentAttempt = 0;
    this.currentDelay = this.config.initialInterval;
  }

  /**
   * Validate configuration options
   * @private
   */
  _validateConfig() {
    if (this.config.initialInterval <= 0) {
      throw new Error('Initial interval must be greater than 0');
    }
    if (this.config.maxDelay <= 0) {
      throw new Error('Max delay must be greater than 0');
    }
    if (this.config.step <= 1) {
      throw new Error('Step must be greater than 1');
    }
    if (this.config.maxRetries !== null && this.config.maxRetries < 0) {
      throw new Error('Max retries must be non-negative or null for infinite retries');
    }
    if (this.config.jitterPercent < 0 || this.config.jitterPercent > 100) {
      throw new Error('Jitter percent must be between 0 and 100');
    }
  }

  /**
   * Add jitter (randomness) to a delay value
   * @param {number} delay - Base delay in milliseconds
   * @returns {number} Delay with jitter applied
   * @private
   */
  _addJitter(delay) {
    if (this.config.jitterPercent === 0) return delay;
    
    const jitterAmount = delay * (this.config.jitterPercent / 100);
    const randomJitter = (Math.random() * 2 - 1) * jitterAmount;
    return Math.max(1, Math.round(delay + randomJitter));
  }

  /**
   * Clean up any pending timeouts
   * @private
   */
  _cleanup() {
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
  }

  /**
   * Reset the instance state for a new execution
   * @private
   */
  _reset() {
    this.isCancelled = false;
    this.isRunning = false;
    this.currentAttempt = 0;
    this.currentDelay = this.config.initialInterval;
    this._cleanup();
  }

  /**
   * Wait for a specified delay with cancellation support
   * @param {number} delay - Delay in milliseconds
   * @returns {Promise} Promise that resolves after delay or rejects if cancelled
   * @private
   */
  _wait(delay) {
    return new Promise((resolve, reject) => {
      const actualDelay = this._addJitter(delay);
      
      this.currentTimeout = setTimeout(() => {
        this.currentTimeout = null;
        if (this.isCancelled) {
          reject(new Error('Exponential backoff was cancelled'));
        } else {
          resolve();
        }
      }, actualDelay);
    });
  }

  /**
   * Execute a callback with exponential backoff retry logic
   * @param {Function} callback - The function to execute with retries
   * @returns {Object} Object containing promise and cancel function { promise, cancel }
   */
  execute(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback function is required');
    }

    if (this.isRunning) {
      throw new Error('ExponentialBackoff instance is already running. Create a new instance or wait for completion.');
    }

    this._reset();
    this.isRunning = true;

    // Main execution promise
    const promise = this._executeWithRetry(callback);

    // Cancel function
    const cancel = () => {
      this.isCancelled = true;
      this._cleanup();
    };

    return { promise, cancel };
  }

  /**
   * Internal retry execution logic
   * @param {Function} callback - The callback to execute
   * @returns {Promise} Promise that resolves with callback result or rejects
   * @private
   */
  async _executeWithRetry(callback) {
    try {
      while (this.config.maxRetries === null || this.currentAttempt <= this.config.maxRetries) {
        // Check if cancelled before each attempt
        if (this.isCancelled) {
          throw new Error('Exponential backoff was cancelled');
        }

        try {
          // Attempt to execute the callback
          const result = await callback(this.currentAttempt);
          this._cleanup();
          this.isRunning = false;
          return result;
        } catch (error) {
          this.currentAttempt++;
          
          // If we've exceeded max retries, handle failure
          if (this.config.maxRetries !== null && this.currentAttempt > this.config.maxRetries) {
            this._cleanup();
            this.isRunning = false;
            
            if (this.config.onMaxRetriesReached) {
              await this.config.onMaxRetriesReached(error, this.currentAttempt - 1);
            }
            throw new Error(`Max retries (${this.config.maxRetries}) exceeded. Last error: ${error.message}`);
          }

          // Check if cancelled before retry
          if (this.isCancelled) {
            this._cleanup();
            this.isRunning = false;
            throw new Error('Exponential backoff was cancelled');
          }

          // Call onRetry callback if provided
          if (this.config.onRetry) {
            await this.config.onRetry(error, this.currentAttempt, this.currentDelay);
          }

          // Wait for the current delay period
          await this._wait(this.currentDelay);

          // Calculate next delay with exponential backoff, capped at maxDelay
          this.currentDelay = Math.min(this.currentDelay * this.config.step, this.config.maxDelay);
        }
      }
    } catch (error) {
      this._cleanup();
      this.isRunning = false;
      throw error;
    }
  }

  /**
   * Cancel any running execution
   */
  cancel() {
    this.isCancelled = true;
    this._cleanup();
  }

  /**
   * Get current execution state
   * @returns {Object} Current state information
   */
  getState() {
    return {
      isRunning: this.isRunning,
      isCancelled: this.isCancelled,
      currentAttempt: this.currentAttempt,
      currentDelay: this.currentDelay,
      config: { ...this.config }
    };
  }

  /**
   * Update configuration (only when not running)
   * @param {Object} newConfig - New configuration options
   */
  updateConfig(newConfig) {
    if (this.isRunning) {
      throw new Error('Cannot update configuration while execution is running');
    }
    
    this.config = { ...this.config, ...newConfig };
    this._validateConfig();
  }

  /**
   * Create a new ExponentialBackoff instance with preset configuration
   * @param {Object} defaultConfig - Default configuration options
   * @returns {Function} Factory function that creates configured instances
   */
  static createFactory(defaultConfig = {}) {
    return function(overrideConfig = {}) {
      const mergedConfig = { ...defaultConfig, ...overrideConfig };
      return new ExponentialBackoff(mergedConfig);
    };
  }

  /**
   * Utility method to execute a callback with a one-time backoff configuration
   * @param {Function} callback - The callback function to execute
   * @param {Object} config - Configuration options
   * @returns {Object} Object containing promise and cancel function { promise, cancel }
   */
  static execute(callback, config = {}) {
    const instance = new ExponentialBackoff(config);
    return instance.execute(callback);
  }
}

// Example usage:

// Basic usage with class instantiation
/*
const backoff = new ExponentialBackoff({
  initialInterval: 500,
  maxDelay: 10000,
  step: 1.5,
  maxRetries: 5,
  jitterPercent: 10
});

const { promise, cancel } = backoff.execute(async (attempt) => {
  console.log(`Attempt ${attempt + 1}`);
  
  // Simulate an operation that might fail
  if (Math.random() < 0.7) {
    throw new Error('Operation failed');
  }
  
  return 'Success!';
});

// Cancel after 10 seconds
setTimeout(() => cancel(), 10000);

promise
  .then(result => console.log('Final result:', result))
  .catch(error => console.error('Failed or cancelled:', error.message));
*/

// One-time execution with static method
/*
const { promise, cancel } = ExponentialBackoff.execute(
  async (attempt) => {
    console.log(`API call attempt ${attempt + 1}`);
    const response = await fetch('/api/data');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },
  {
    initialInterval: 1000,
    maxRetries: null,  // Infinite retries
    maxDelay: 30000,
    jitterPercent: 15,
    onRetry: (error, attempt, delay) => {
      console.log(`Retry ${attempt} after ${delay}ms. Error: ${error.message}`);
    }
  }
);
*/

// Using factory for reusable configurations
/*
const apiBackoffFactory = ExponentialBackoff.createFactory({
  initialInterval: 1000,
  maxDelay: 15000,
  step: 2,
  maxRetries: 3,
  jitterPercent: 10
});

const backoffInstance = apiBackoffFactory();
const { promise, cancel } = backoffInstance.execute(async () => {
  return await someApiCall();
});
*/

// Reusing the same instance for multiple operations
/*
const backoff = new ExponentialBackoff({ maxRetries: 3 });

// First operation
const { promise: promise1, cancel: cancel1 } = backoff.execute(async () => {
  return await operation1();
});

await promise1;

// Second operation (after first completes)
const { promise: promise2, cancel: cancel2 } = backoff.execute(async () => {
  return await operation2();
});
*/

export default ExponentialBackoff;